import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/uxp-photoshop/uxp-photoshop/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "photoshop-api-changelog"
    }}>{`Photoshop API Changelog`}</h1>
    <h2 {...{
      "id": "introduction"
    }}>{`Introduction`}</h2>
    <p>{`As of now, this documentation site describes API functionality that is available for use to a select number of participants in `}<a parentName="p" {...{
        "href": "https://www.adobeprerelease.com/"
      }}>{`Adobe Prerelease community`}</a>{`. We will start to evolve the site to support users and plugin developers outside the Prerelease group, once more functionality is available, and when we can be more confident our changes are stable for the wider audience.`}</p>
    <p>{`If you would like to join the conversation, please consider joining the `}<a parentName="p" {...{
        "href": "https://www.adobeprerelease.com/"
      }}>{`Adobe Prerelease community`}</a>{`!`}</p>
    <h2 {...{
      "id": "photoshop-234-june-2022"
    }}>{`Photoshop 23.4 (June 2022)`}</h2>
    <h3 {...{
      "id": "convertunits-method-on-the-photoshop-application-object"
    }}>{`convertUnits method on the Photoshop Application object`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/photoshop"
        }}>{`Application.convertUnits`}</a>{` is a method for converting the pixel values found in the UXP API to other units. For example, use this routine for converting a document's width from pixels to inches.`}</li>
    </ul>
    <h2 {...{
      "id": "photoshop-233-april-2022"
    }}>{`Photoshop 23.3 (April 2022)`}</h2>
    <h3 {...{
      "id": "paths-support"
    }}>{`Paths support`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/document/"
        }}>{`Document.pathItems`}</a>{` property is now added, giving access to same APIs from ExtendScript to work with paths.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.pathItems`}</inlineCode>{` provides the collection class `}<a parentName="li" {...{
          "href": "../classes/pathitems/"
        }}>{`PathItems`}</a>{`, which is a collection of `}<a parentName="li" {...{
          "href": "../classes/pathitem/"
        }}>{`PathItem`}</a>{` objects.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`PathItem.subPathItems`}</inlineCode>{` provides the collection class `}<a parentName="li" {...{
          "href": "../classes/subpathitems/"
        }}>{`SubPathItems`}</a>{`, which is a collection of `}<a parentName="li" {...{
          "href": "../classes/subpathitem/"
        }}>{`SubPathItem`}</a>{` objects.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`SubPathItem.pathPoints`}</inlineCode>{` provides the collection class `}<a parentName="li" {...{
          "href": "../classes/pathpoints/"
        }}>{`PathPoints`}</a>{`, which is a collection of `}<a parentName="li" {...{
          "href": "../classes/pathpoint/"
        }}>{`PathPoint`}</a>{` objects.`}</li>
      <li parentName="ul">{`Create new paths in the document using `}<a parentName="li" {...{
          "href": "../classes/pathitems/#add"
        }}><inlineCode parentName="a">{`PathItems.add`}</inlineCode></a>{` method by providing `}<inlineCode parentName="li">{`PathPointInfo`}</inlineCode>{` and `}<inlineCode parentName="li">{`SubPathInfo`}</inlineCode>{` objects. Constructors for them can be found in `}<a parentName="li" {...{
          "href": "../classes/photoshop/"
        }}>{`app`}</a>{` object.`}</li>
    </ul>
    <h3 {...{
      "id": "load-on-launch-support"
    }}>{`Load on Launch support`}</h3>
    <p>{`You can now set your plugin manifest to load your plugin on app launch. See `}<a parentName="p" {...{
        "href": "../../guides/uxp_guide/uxp-misc/manifest-v4/photoshop-manifest/"
      }}>{`Photoshop Manifest`}</a>{` for details.`}</p>
    <h3 {...{
      "id": "batchplay-development-helper-copy-as-javascript"
    }}>{`batchPlay development helper: Copy as Javascript`}</h3>
    <p>{`It is now possible to use the Photoshop Actions panel to help build your plugin. Record valid actions, and let Photoshop generate readable and valid batchPlay to use in your UXP plugin. See the `}<a parentName="p" {...{
        "href": "../media/batchplay.md#accepted-action-descriptors"
      }}>{`batchPlay documentation`}</a>{` under "Accepted action descriptors".`}</p>
    <h3 {...{
      "id": "core-event-useridle"
    }}>{`Core event: "userIdle"`}</h3>
    <p>{`This event is generated when Photoshop detects that a user becomes idle, while Photoshop is the foreground application. A plugin must specify a number of idle seconds, and then may choose to batch process-intensive tasks. See `}<a parentName="p" {...{
        "href": "../media/photoshopcore/"
      }}>{`photoshopCore`}</a>{`.`}</p>
    <h3 {...{
      "id": "interactive-mode-for-executeasmodal"
    }}>{`Interactive Mode for executeAsModal`}</h3>
    <p>{`As an alterative to a UI-blocking progress bar when a plugin is within a Modal Execution scope, `}<inlineCode parentName="p">{`interactiveMode`}</inlineCode>{` can be requested to allow for user interaction in specific circumstances. See `}<a parentName="p" {...{
        "href": "../media/executeasmodal#interactive-mode/"
      }}>{`Interactive Mode`}</a>{`.`}</p>
    <h2 {...{
      "id": "photoshop-232-february-2022"
    }}>{`Photoshop 23.2 (February 2022)`}</h2>
    <h3 {...{
      "id": "channel-support"
    }}>{`Channel support`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/channels/"
        }}>{`Channels collections`}</a>{` behave like other collections in the API. Channels further supports `}<inlineCode parentName="li">{`channels.add()`}</inlineCode>{` and `}<inlineCode parentName="li">{`channels.removeAll()`}</inlineCode>{`. `}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/channel"
        }}>{`Channel class`}</a>
        <ul parentName="li">
          <li parentName="ul">{`Supports four types of channels, as listed in `}<a parentName="li" {...{
              "href": "../modules/constants/#channeltype"
            }}>{`Constants.ChannelType`}</a>{`: `}<inlineCode parentName="li">{`COMPONENT`}</inlineCode>{`, `}<inlineCode parentName="li">{`MASKEDAREA`}</inlineCode>{`, `}<inlineCode parentName="li">{`SELECTEDAREA`}</inlineCode>{`, `}<inlineCode parentName="li">{`SPOTCOLOR`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`Adds the following Channel properties: `}<inlineCode parentName="li">{`name`}</inlineCode>{`, `}<inlineCode parentName="li">{`type`}</inlineCode>{`, `}<inlineCode parentName="li">{`visible`}</inlineCode>{`, `}<inlineCode parentName="li">{`histogram`}</inlineCode>{`, `}<inlineCode parentName="li">{`color`}</inlineCode>{`, `}<inlineCode parentName="li">{`opacity`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`Adds the following Channel methods: `}<inlineCode parentName="li">{`remove()`}</inlineCode>{`, `}<inlineCode parentName="li">{`merge()`}</inlineCode>{` (for ChannelType.SPOTCOLOR).`}</li>
          <li parentName="ul">{`Adds to the Document class: `}<inlineCode parentName="li">{`get compositeChannels`}</inlineCode>{`, `}<inlineCode parentName="li">{`get activeChannels`}</inlineCode>{`, `}<inlineCode parentName="li">{`set activeChannels`}</inlineCode>{`.`}</li>
        </ul>
      </li>
    </ul>
    <h3 {...{
      "id": "document-transformations"
    }}>{`Document Transformations`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`Document.crop(bounds: Bounds, angle, width, height)`}</inlineCode>{`: Crops the document. The `}<inlineCode parentName="li">{`bounds`}</inlineCode>{` paramter is an array of four coordinates for the region remaining after cropping, `}{`[left, top, right, bottom]`}{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.resizeCanvas(width, height, Constants.AnchorPosition)`}</inlineCode>{`: Changes the size of the canvas to display more or less of the image but does not change the image size. See `}<inlineCode parentName="li">{`resizeImage`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.resizeImage(width, heigh, resolution, Constants.ResampleMethod, amount)`}</inlineCode>{`: Changes the size of the image. The `}<inlineCode parentName="li">{`amount`}</inlineCode>{` of noise value when using preserve details (Range: 0 1 100).`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.rotateCanvas(angle)`}</inlineCode>{`: Rotates the image and resizes the canvas to fit the resized image.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.trim(Constants.TrimType, top, left, bottom, right)`}</inlineCode>{`: If `}<inlineCode parentName="li">{`TrimType.TRANSPARENT`}</inlineCode>{`, trims the transparent area around the image on the specified sides of the canvas. Trims surround areas that color match the top left corner or bottom right corner pixel if `}<inlineCode parentName="li">{`TrimType.TOPLEFT`}</inlineCode>{` or `}<inlineCode parentName="li">{`TrimType.BOTTOMRIGHT`}</inlineCode>{` is specified, respectively.`}</li>
    </ul>
    <h3 {...{
      "id": "layer-cut-and-copy"
    }}>{`Layer cut and copy`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`layer.cut()`}</inlineCode>{`: Cut the pixels out of the layer.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`layer.copy(merge: boolean)`}</inlineCode>{`: Copy the layer or if a selection is present the merge option will copy from multiple layers..`}</li>
    </ul>
    <h3 {...{
      "id": "layer-methods"
    }}>{`Layer Methods`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`layer.duplicate(relativeObject?: Document | Layer, insertionLocation?: Constants.ElementPlacement, name?: string)`}</inlineCode>{`: Duplicates the layer, creating a copy above it in layer stack and returns the newly created layer.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`layer.delete()`}</inlineCode>{`: Deletes this layer from the document.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`layer.link()`}</inlineCode>{`: Creates a link between this layer and the target layer if not already linked and returns a list of layers linked to this layer.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`layer.unlink()`}</inlineCode>{`: Unlinks the layer from any existing links.`}</li>
    </ul>
    <h3 {...{
      "id": "document-paste"
    }}>{`Document paste`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`document.paste(intoSelection: boolean)`}</inlineCode>{`: Pastes the contents of the clipboard into the document. If the optional argument is set to true and a selection is active, the contents are pasted into the selection.`}</li>
    </ul>
    <h3 {...{
      "id": "document-methods"
    }}>{`Document Methods`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`Document.flatten()`}</inlineCode>{`: Merges all visible layers in the document into a single layer.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.mergeVisibleLayers()`}</inlineCode>{`: Flattens all visible layers in the document`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.revealAll()`}</inlineCode>{`: Expands the document to show clipped sections.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.rasterizeAllLayers()`}</inlineCode>{`: Rasterizes all layers.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.splitChannels()`}</inlineCode>{`: Splits the document channels into separate, single-channel documents.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.duplicate()`}</inlineCode>{`: Creates a copy of the document, switching to the copy.`}</li>
    </ul>
    <h3 {...{
      "id": "solidcolor-api"
    }}><a parentName="h3" {...{
        "href": "../classes/solidcolor"
      }}>{`SolidColor`}</a>{` API`}</h3>
    <ul>
      <li parentName="ul">{`Add require('photoshop').app.SolidColor constructor, which allows for creation of SolidColor objects.`}</li>
      <li parentName="ul">{`APIs that accept colors now also accept SolidColor objects.`}
        <ul parentName="li">
          <li parentName="ul">{`app.foregroundColor and app.backgroundColor.`}</li>
          <li parentName="ul">{`app.documents.add `}<inlineCode parentName="li">{`fillColor`}</inlineCode>{` property.`}</li>
          <li parentName="ul">{`Document.saveAs methods that use colors.`}</li>
        </ul>
      </li>
      <li parentName="ul"><inlineCode parentName="li">{`require('photoshop').core.convertColor`}</inlineCode>{` low level API added, used internally by SolidColor. It uses app color
settings to convert between color spaces.`}</li>
    </ul>
    <h2 {...{
      "id": "adobe-photoshop-beta-build-65-sep-3-2021"
    }}>{`Adobe Photoshop Beta Build 65: Sep 3, 2021`}</h2>
    <h3 {...{
      "id": "layer-transformations"
    }}>{`Layer Transformations`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`layer.rotate(angle: number, anchor?: Constants.AnchorPosition)`}</inlineCode>{`: Rotate by `}<inlineCode parentName="li">{`angle`}</inlineCode>{` about `}<inlineCode parentName="li">{`anchor`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`layer.scale(width: number, height: number, anchor?: Constants.AnchorPosition)`}</inlineCode>{`: Scale layer by `}<inlineCode parentName="li">{`width`}</inlineCode>{` percent and `}<inlineCode parentName="li">{`height`}</inlineCode>{` percent of the image, relative to `}<inlineCode parentName="li">{`anchor`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`layer.translate(horizontal: number, vertical: number)`}</inlineCode>{`: Translate layer by `}<inlineCode parentName="li">{`horizontal`}</inlineCode>{` and `}<inlineCode parentName="li">{`vertical`}</inlineCode>{` offset (by pixel or percent).`}</li>
    </ul>
    <h3 {...{
      "id": "appfonts-and-textfont-classes"
    }}>{`app.fonts and TextFont classes`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`app.fonts`}</inlineCode>{` getter: Returns all installed fonts`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`TextFont`}</inlineCode>{` class and `}<inlineCode parentName="li">{`TextFonts`}</inlineCode>{` collection class`}</li>
    </ul>
    <h3 {...{
      "id": "document-properties"
    }}>{`Document Properties`}</h3>
    <ul>
      <li parentName="ul">{`Document.bitsPerChannel (read-write): The bits per color channel (type Constants.BitsPerChannelType)`}</li>
      <li parentName="ul">{`Document.cloudDocument (read only): This document is in the Adobe Creative Cloud (type boolean)`}</li>
      <li parentName="ul">{`Document.cloudWorkAreaDirectory (read only): Local directory for this cloud document (type string)`}</li>
      <li parentName="ul">{`Document.histogram (read only): A histogram containing the number of pixels at each color intensity level for the composite channel. The array contains 256 members.`}</li>
      <li parentName="ul">{`Document.name (read only): The name of the document (type string)`}</li>
      <li parentName="ul">{`Document.path (read only): Full file system path to this document, or the identifier if it is a cloud document (type string)`}</li>
      <li parentName="ul">{`Document.quickMaskMode (read-write): The state of Quick Mask mode. If true, the app is in Quick Mask mode (type boolean)`}</li>
      <li parentName="ul">{`Document.saved (read only): True if the document has been saved since the last change (type boolean)`}</li>
    </ul>
    <h3 {...{
      "id": "document-color-profile-apis"
    }}>{`Document Color Profile APIs`}</h3>
    <ul>
      <li parentName="ul">{`Document.mode property (read only)`}</li>
      <li parentName="ul">{`Document.colorProfileType and Document.colorProfileName properties (read-write)`}</li>
      <li parentName="ul">{`Document.changeMode(), Document.convertProfile(), and Document.trap() methods`}</li>
    </ul>
    <h2 {...{
      "id": "adobe-photoshop-beta-build-64-aug-6-2021"
    }}>{`Adobe Photoshop Beta Build 64: Aug 6, 2021`}</h2>
    <h3 {...{
      "id": "documentsuspendhistory"
    }}><a parentName="h3" {...{
        "href": "../classes/document/#suspendhistory"
      }}>{`Document.suspendHistory`}</a></h3>
    <p>{`This API wraps around `}<a parentName="p" {...{
        "href": "https://adobe.ly/psmodal"
      }}><inlineCode parentName="a">{`core.executeAsModal`}</inlineCode></a>{` within a single document to make it easier for you to run your code under a single history state. Unlike it's ExtendScript predecessor, this method takes a Javascript function, rather than a script string.`}</p>
    <h3 {...{
      "id": "layer-move-apis"
    }}><a parentName="h3" {...{
        "href": "../classes/layer/#move"
      }}>{`Layer Move APIs`}</a></h3>
    <ul>
      <li parentName="ul">{`Arrange layers with `}<inlineCode parentName="li">{`layer.move(targetLayer, Constants.ElementPlacement)`}</inlineCode>{`. Added `}<inlineCode parentName="li">{`Constants.ElementPlacement`}</inlineCode>{` for moving layers: `}<inlineCode parentName="li">{`"placeAfter"`}</inlineCode>{` (place below a layer, below group if group layer), `}<inlineCode parentName="li">{`"placeBefore"`}</inlineCode>{` (place above a layer, above group if group layer), and `}<inlineCode parentName="li">{`"placeInside"`}</inlineCode>{` (inside group layers only).`}</li>
      <li parentName="ul">{`Send a layer to the back or bring a layer to the front with `}<inlineCode parentName="li">{`layer.sendToBack()`}</inlineCode>{` and `}<inlineCode parentName="li">{`layer.bringToFront()`}</inlineCode>{`. If `}<inlineCode parentName="li">{`layer`}</inlineCode>{` is within a group, the commands will operate within that group.`}</li>
    </ul>
    <h2 {...{
      "id": "adobe-photoshop-prerelease-build-61-jun-24-2021"
    }}>{`Adobe Photoshop Prerelease Build 61: Jun 24, 2021`}</h2>
    <h3 {...{
      "id": "updated-dom-api"
    }}>{`Updated DOM API`}</h3>
    <ul>
      <li parentName="ul">{`New `}<inlineCode parentName="li">{`Layers`}</inlineCode>{` collection, the old `}<inlineCode parentName="li">{`layerTree`}</inlineCode>{` object is now called `}<inlineCode parentName="li">{`layers`}</inlineCode>{` but as a collection using the JavaScript Proxy object. See below for introduction of collections. The old `}<inlineCode parentName="li">{`layers`}</inlineCode>{` behavior is no longer part of the DOM. Most scripts can be updated to replace `}<inlineCode parentName="li">{`layerTree`}</inlineCode>{` with `}<inlineCode parentName="li">{`layers`}</inlineCode>{`.  A document and a layer, if the layer kind is group, will have a valid `}<inlineCode parentName="li">{`layers`}</inlineCode>{` collection. A `}<inlineCode parentName="li">{`pixel`}</inlineCode>{` layer will have `}<inlineCode parentName="li">{`null`}</inlineCode>{` for the `}<inlineCode parentName="li">{`layers`}</inlineCode>{` property. A collection has the same semantics as an Array. The additional ExtendScript routines of `}<inlineCode parentName="li">{`add`}</inlineCode>{` and `}<inlineCode parentName="li">{`getByName`}</inlineCode>{` are also available. Use `}<inlineCode parentName="li">{`LayerKind.GROUP`}</inlineCode>{` on a `}<inlineCode parentName="li">{`layer`}</inlineCode>{` to see if any `}<inlineCode parentName="li">{`layers`}</inlineCode>{` are available.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`GroupLayer`}</inlineCode>{` has been removed. Use `}<inlineCode parentName="li">{`activeLayer.kind === LayerKind.GROUP`}</inlineCode>{` instead.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Constants.LayerKind`}</inlineCode>{`, similar to ExtendScript version, but with "group" layer addition.`}</li>
      <li parentName="ul">{`Layer APIs`}
        <ul parentName="li">
          <li parentName="ul"><em parentName="li">{`new`}</em>{` locked, read-only way to tell if any of the properties are locked`}</li>
          <li parentName="ul">{`allLocked, pixelsLocked, positionLocked, transparentPixelsLocked properties`}</li>
          <li parentName="ul">{`Some existing properties exposed in documentation`}</li>
        </ul>
      </li>
    </ul>
    <h2 {...{
      "id": "adobe-photoshop-prerelease-build-60-jun-10-2021"
    }}>{`Adobe Photoshop Prerelease Build 60: Jun 10, 2021`}</h2>
    <p>{`This update contains changes that were made since the MAX 2020 (Adobe Photoshop 22.0) release.`}</p>
    <h3 {...{
      "id": "breaking-changes"
    }}>{`Breaking changes`}</h3>
    <p>{`To use the new set of APIs, your project's `}<inlineCode parentName="p">{`manifest.json`}</inlineCode>{` must be updated to include the following under the `}<inlineCode parentName="p">{`host`}</inlineCode>{` object:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    "data" : {
        "apiVersion": 2
    }
`}</code></pre>
    <p>{`Further, your `}<inlineCode parentName="p">{`manifest.json`}</inlineCode>{` must use version 4:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`  "manifestVersion": 4
`}</code></pre>
    <p>{`To roll back to the MAX 2020 version of the Photoshop DOM API, set the `}<inlineCode parentName="p">{`apiVersion`}</inlineCode>{` to 1.`}</p>
    <h3 {...{
      "id": "updated-dom-api-1"
    }}>{`Updated DOM API`}</h3>
    <ul>
      <li parentName="ul">{`Collections`}
        <ul parentName="li">
          <li parentName="ul">{`We're introducing collection classes built using `}<a parentName="li" {...{
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Proxy"
            }}>{`Proxies`}</a>{`, to make it easier to interact with the DOM.`}</li>
          <li parentName="ul">{`Try iterating through all the documents as such: `}<inlineCode parentName="li">{`app.documents.forEach(h => console.log(h.title));`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/historystates/"
        }}>{`HistoryStates collections`}</a>{` and `}<a parentName="li" {...{
          "href": "../classes/historystate/"
        }}>{`HistoryState API`}</a>
        <ul parentName="li">
          <li parentName="ul">{`We've built HistoryStates to mirror ExtendScript functionality. `}<inlineCode parentName="li">{`length`}</inlineCode>{`, `}<inlineCode parentName="li">{`parent`}</inlineCode>{`, and `}<inlineCode parentName="li">{`getByName(name)`}</inlineCode>{` are provided. You can also use indices as such: `}<inlineCode parentName="li">{`activeDocument.historyStates[2]`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`HistoryState provides `}<inlineCode parentName="li">{`name`}</inlineCode>{`, `}<inlineCode parentName="li">{`parent`}</inlineCode>{`, `}<inlineCode parentName="li">{`snapshot`}</inlineCode>{`.`}</li>
        </ul>
      </li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/documents/"
        }}>{`Documents collections`}</a>
        <ul parentName="li">
          <li parentName="ul">{`Documents also follows ExtendScript functionality. `}<inlineCode parentName="li">{`length`}</inlineCode>{`, `}<inlineCode parentName="li">{`add`}</inlineCode>{`, `}<inlineCode parentName="li">{`getByName(name)`}</inlineCode>{`, `}<inlineCode parentName="li">{`parent`}</inlineCode>{` are provided.`}</li>
        </ul>
      </li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../modules/constants/"
        }}>{`Constants module`}</a>{`, collecting all enumerations and constants used in DOM API. Accessible via `}<inlineCode parentName="li">{`require("photoshop").constants`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Updated `}<a parentName="li" {...{
          "href": "../classes/photoshop/#createdocument"
        }}>{`app.createDocument / app.add`}</a>
        <ul parentName="li">
          <li parentName="ul">{`Create a document with no params for a default document, with a predefined preset, or by providing a host of `}<a parentName="li" {...{
              "href": "../objects/documentcreateoptions/"
            }}>{`DocumentCreateOptions`}</a>{`).`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Updated `}<a parentName="li" {...{
          "href": "../classes/document/#save"
        }}>{`document.save`}</a>{` and `}<a parentName="li" {...{
          "href": "../classes/document/#saveas"
        }}>{`document.saveAs`}</a>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`save`}</inlineCode>{` no longer infers the file type from the requested file name. Instead, it invokes a save dialog for unsaved files, and performs a save operation for saved, modified files.`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`saveAs`}</inlineCode>{` is provided for `}<inlineCode parentName="li">{`bmp`}</inlineCode>{`, `}<inlineCode parentName="li">{`gif`}</inlineCode>{`, `}<inlineCode parentName="li">{`jpg`}</inlineCode>{`, `}<inlineCode parentName="li">{`png`}</inlineCode>{`, `}<inlineCode parentName="li">{`psb`}</inlineCode>{`, `}<inlineCode parentName="li">{`psd`}</inlineCode>{` formats. Use SaveOptions objects to request specific saves (e.g. `}<a parentName="li" {...{
              "href": "../objects/photoshopsaveoptions/"
            }}>{`PhotoshopSaveOptions`}</a>{`).`}</li>
        </ul>
      </li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer"
        }}>{`layer.id getter`}</a></li>
      <li parentName="ul">{`Updated `}<a parentName="li" {...{
          "href": "../classes/document/#createlayer"
        }}>{`document.createLayer`}</a>{` and `}<a parentName="li" {...{
          "href": "../classes/document/#createlayergroup"
        }}>{`document.createLayerGroup`}</a>
        <ul parentName="li">
          <li parentName="ul">{`With respective Create options: `}<a parentName="li" {...{
              "href": "../objects/layercreateoptions/"
            }}>{`LayerCreateOptions`}</a>{` and `}<a parentName="li" {...{
              "href": "../objects/grouplayercreateoptions"
            }}>{`GroupLayerCreateOptions`}</a>{`.`}</li>
          <li parentName="ul">{`Collection access updates for Layer and Layer-like items are planned for upcoming releases.`}</li>
        </ul>
      </li>
    </ul>
    <h4 {...{
      "id": "photoshop-core-updates"
    }}>{`Photoshop Core updates`}</h4>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../media/executeasmodal/"
        }}>{`Execute as Modal`}</a>{` allows you to create plugins that can be guaranteed exclusive control over Photoshop during execution. It further allows for clear user feedback when this is occuring, and allows plugins to `}<inlineCode parentName="li">{`suspendHistory`}</inlineCode>{` and `}<inlineCode parentName="li">{`resumeHistory`}</inlineCode>{`.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      